import {
  PermissionFeatures,
  PermissionFeatureKeys
} from '../interfaces';
import { decodeTCString } from './decode-tcstring';
import {
  getIdcc,
  getTCString,
  getVendorArray
} from './utils';
import * as Logger from './logger';
import {
  CONFIG_IAB_TCF_VENDOR_AGOF_DAILY_DIGITAL_FACTS,
  CONFIG_IAB_TCF_VENDOR_GOOGLE,
  CONFIG_IAB_TCF_VENDOR_AMAZON,
  CONFIG_IAB_TCF_VENDOR_VERIZON,
  CONFIG_IAB_TCF_VENDOR_DPAG,
  CONFIG_IAB_TCF_VENDOR_INTELLI_AD,
  CONFIG_IAB_TCF_VENDOR_INFONLINE,
  CONFIG_IAB_TCF_VENDOR_OPINARY
} from '../config';

const moduleName = '[Permission Feature] ->';

/**
 * Returns the permission of a permission feature stack
 * @param {PermissionFeatureKeys} pFeature The requested feature stack
 * @param {number | number[]} pId Specific purposeId for some feature types (optional)
 * @param {string} encodedTcString The tcString itself (optional)
 */
export function getPermissionFeature(pFeature: PermissionFeatureKeys, pId?: number | number[], encodedTcString: string | null = getTCString()): boolean {
  if (encodedTcString === null) {
    Logger.debug(moduleName, 'getPermissionFeature: GDPR does not apply, applying consent by default!');
    return true;
  }

  if (!encodedTcString || typeof encodedTcString !== 'string') {
    Logger.warn(moduleName, `getPermissionFeature: No tcString available or not typeof string, applying privacy by default!`);
    return false;
  }

  if (!Object.prototype.hasOwnProperty.call(PermissionFeatures, pFeature)) {
    throw new Error(`getPermissionFeature: Requested permission${!pId ? 'Feature' : ''} '${pFeature}'${pId ? ` with pId '${pId}'` : ''} is not available!`);
  }

  const tcModel = decodeTCString(encodedTcString);
  if (!tcModel) {
    Logger.error(moduleName, 'getPermissionFeature: No valid tcModel, applying privacy by default!');
    return false;
  }

  // If single `pId` is given, wrap it into an array
  if (typeof pId === 'number') {
    pId = [pId];
  }

  // See: PPP-178
  return {
    brainTracking:
      [1, 8, 10].every((i: number) => tcModel.publisherConsents.has(i)) &&
      getIdcc(),

    uimservTracking:
      [1, 3, 5, 7, 8, 9, 10].every((i: number) => tcModel.publisherConsents.has(i)) &&
      getIdcc(),

    agofTracking:
      [1, 7, 8, 9].every((i: number) => tcModel.publisherConsents.has(i)) &&
      [1, 7, 8, 9].every((i: number) => tcModel.purposeConsents.has(i)) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_AGOF_DAILY_DIGITAL_FACTS) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_INFONLINE),

    tgp:
      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].every((i: number) => tcModel.publisherConsents.has(i)) &&
      getIdcc(),

    oewaTracking:
      [1, 8].every((i: number) => tcModel.publisherConsents.has(i)) &&
      [1, 8].every((i: number) => tcModel.purposeConsents.has(i)) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_INFONLINE),

    appmonPerformanceTracing:
      [1, 8].every((i: number) => tcModel.publisherConsents.has(i)),

    googleAnalyticsTracking:
      tcModel.publisherConsents.has(1) &&
      tcModel.purposeConsents.has(3) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_GOOGLE),

    googleAnalyticsTrackingInternational:
      [1, 7, 8, 10].every((i: number) => tcModel.publisherConsents.has(i)) &&
      [1, 7, 8, 10].every((i: number) => tcModel.purposeConsents.has(i)) &&
      tcModel.publisherCustomConsents.has(3),

    editorialPersonalization:
      [1, 5, 6, 8, 10].every((i: number) => tcModel.publisherConsents.has(i)) &&
      tcModel.specialFeatureOptins.has(1) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_AMAZON) &&
      getIdcc(),

    aditionAds:
      [1, 2, 3, 4, 7].every((i: number) => tcModel.purposeConsents.has(i)) &&
      getIdcc(),

    siteSpectTesting:
      [1, 8, 10].every((i: number) => tcModel.publisherConsents.has(i)) &&
      getIdcc(),

    googleAc:
      [1, 2, 3, 4, 7].every((i: number) => tcModel.purposeConsents.has(i)) &&
      tcModel.specialFeatureOptins.has(2) &&
      tcModel.publisherCustomConsents.has(1),

    amazonAds:
      [1, 2, 3, 4, 7].every((i: number) => tcModel.purposeConsents.has(i)) &&
      [1, 2, 3, 4, 7].every((i: number) => tcModel.publisherConsents.has(i)) &&
      tcModel.specialFeatureOptins.has(2) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_AMAZON),

    specialGeoFeature:
      tcModel.specialFeatureOptins.has(1),

    googleAdsConversionTracking:
      [1, 3, 7, 8].every((i: number) => tcModel.publisherConsents.has(i)) &&
      [1, 2].every((i: number) => tcModel.specialFeatureOptins.has(i)) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_GOOGLE),

    searchBasicAds:
      [1, 2, 7].every((i: number) => tcModel.publisherConsents.has(i)) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_AMAZON),

    searchPersonalisedAds:
      [1, 2, 3, 4, 7].every((i: number) => tcModel.publisherConsents.has(i)) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_AMAZON),

    sentryErrorTracing:
      tcModel.publisherCustomConsents.has(1),

    internalRetargeting:
      [1, 2, 3, 4, 7, 10].every((i: number) => tcModel.publisherConsents.has(i)) &&
      getIdcc(),

    googleBasicAds:
      tcModel.publisherConsents.has(1) &&
      tcModel.purposeConsents.has(1) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_GOOGLE),

    googleAdRemarketing:
      [1, 2, 3, 4, 5, 7].every((i: number) => tcModel.publisherConsents.has(i)) &&
      tcModel.publisherCustomConsents.has(1) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_GOOGLE),

    searchBasicAdsAmazon:
      [1, 2, 7, 9, 10].every((i: number) => tcModel.purposeConsents.has(i)) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_AMAZON),

    searchBasicAdsVerizon:
      [1, 2, 7, 9, 10].every((i: number) => tcModel.purposeConsents.has(i)) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_VERIZON),

    searchBasicAdsGoogle:
      [1, 2, 7, 9, 10].every((i: number) => tcModel.purposeConsents.has(i)) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_GOOGLE),

    searchYahooImage:
      tcModel.purposeConsents.has(1) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_VERIZON),

    firstPartyCookie:
      tcModel.publisherConsents.has(1),

    thirdPartyCookie:
      tcModel.publisherConsents.has(1) &&
      tcModel.purposeConsents.has(1),

    vgwortTracking:
      [1, 2, 3, 4, 7, 10].every((i: number) => tcModel.publisherConsents.has(i)) &&
      getIdcc(),

    dpagSync:
      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].every((i: number) => tcModel.publisherConsents.has(i)) &&
      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].every((i: number) => tcModel.purposeConsents.has(i)) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_INTELLI_AD) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_DPAG),

    opinary:
      [1, 2, 5, 6, 7, 8, 9, 10].every((i: number) => tcModel.purposeConsents.has(i)) &&
      [1, 2].every((i: number) => tcModel.specialFeatureOptins.has(i)) &&
      tcModel.vendorConsents.has(CONFIG_IAB_TCF_VENDOR_OPINARY),

    fullConsent:
      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].every((i: number) => tcModel.publisherConsents.has(i)) &&
      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].every((i: number) => tcModel.purposeConsents.has(i)) &&
      [1, 2].every((i: number) => tcModel.specialFeatureOptins.has(i)) &&
      tcModel.publisherCustomConsents.has(1) &&
      getVendorArray().every((i: number) => tcModel.vendorConsents.has(i)) &&
      getIdcc(),

    publisher:
      pId && pId.length > 0 ? pId.every((i: number) => tcModel.publisherConsents.has(i)) : false,

    publisherCustomPurpose:
      pId && pId.length > 0 ? pId.every((i: number) => tcModel.publisherCustomConsents.has(i)) : false,

    purpose:
      pId && pId.length > 0 ? pId.every((i: number) => tcModel.purposeConsents.has(i)) : false,

    vendor:
      pId && pId.length > 0 ? pId.every((i: number) => tcModel.vendorConsents.has(i)) : false,

    special:
      pId && pId.length > 0 ? pId.every((i: number) => tcModel.specialFeatureOptins.has(i)) : false

  }[pFeature];
}

/**
 * Returns all available permission features
 * @param {string} encodedTcString The encoded TCString (optional)
 */
export function getPermissionFeatures(encodedTcString: string = getTCString()): { [key: string]: boolean }[] {
  return Object
    .keys(PermissionFeatures)
    .filter((pFeature: PermissionFeatureKeys) => isNaN(parseInt(pFeature)))
    .map((pFeature: PermissionFeatureKeys) => {
      let pIdOverride = null;
      if (pFeature === 'publisher' || pFeature === 'purpose') {
        pIdOverride = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      } else if (pFeature === 'special' || pFeature === 'publisherCustomPurpose') {
        pIdOverride = [1, 2];
      }
      return {
        [pFeature]: getPermissionFeature(pFeature, pIdOverride, encodedTcString)
      }
    });
}
